import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Mail,
  MessageSquare,
  Send,
  Loader2,
  CheckCircle2,
  Building2,
  Phone,
} from 'lucide-react';
import Seo from '../components/Seo';

const SUPPORT_EMAIL = "support@ineedasocialworker.com";

// Track contact analytics
const trackContactEvent = async (eventType, source) => {
  try {
    await axios.post(`${API_URL}/analytics/contact`, {
      event_type: eventType,
      source: source
    });
  } catch (error) {
    console.error('Contact analytics tracking failed:', error);
  }
};

const ContactUs = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [supportUser, setSupportUser] = useState(null);

  useEffect(() => {
    // Pre-fill form if user is logged in
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
      }));
    }
    fetchSupportUser();
    
    // Track page view
    trackContactEvent('page_view', 'contact_page');
  }, [user]);

  const fetchSupportUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/support-user`);
      setSupportUser(response.data);
    } catch (error) {
      console.error('Error fetching support user:', error);
    }
  };

  const handleEmailClick = () => {
    trackContactEvent('email_click', 'contact_page');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageSupport = () => {
    if (!isAuthenticated) {
      toast.info('Please log in to send a message');
      navigate('/login');
      return;
    }
    
    if (supportUser?.user_id) {
      navigate(`/messages?recipient=${supportUser.user_id}`);
    } else {
      toast.error('Support messaging is currently unavailable');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-2xl p-8 sm:p-12 shadow-sm border border-gray-100 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-serif text-3xl font-bold text-gray-900 mb-4">
              Message Sent!
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you for contacting us. We've received your message and will get back to you within 1-2 business days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-primary hover:bg-primary-hover text-white"
              >
                Back to Home
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSubmitted(false);
                  setFormData({ name: user?.name || '', email: user?.email || '', subject: '', message: '' });
                }}
              >
                Send Another Message
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        title="Contact Us"
        description="Get in touch with the I Need A Social Worker team — questions about jobs, employer memberships, license verification, supervision or community resources."
        path="/contact"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl font-bold text-gray-900 mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have a question or need assistance? We're here to help. Choose your preferred method to reach us.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Methods */}
          <div className="lg:col-span-1 space-y-6">
            {/* Email Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Email Us</h3>
                  <p className="text-sm text-gray-500">Direct email support</p>
                </div>
              </div>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                onClick={handleEmailClick}
                className="text-primary hover:text-primary-hover font-medium text-sm break-all"
                data-testid="support-email-link"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>

            {/* In-App Message Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">In-App Message</h3>
                  <p className="text-sm text-gray-500">Chat with our support team</p>
                </div>
              </div>
              <Button
                onClick={handleMessageSupport}
                variant="outline"
                className="w-full border-secondary text-secondary hover:bg-secondary hover:text-white"
                data-testid="message-support-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Message Support
              </Button>
              {!isAuthenticated && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Login required to send messages
                </p>
              )}
            </div>

            {/* Company Info Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Company Info</h3>
                  <p className="text-sm text-gray-500">About us</p>
                </div>
              </div>
              <div className="space-y-3 text-sm text-gray-600">
                <p className="font-medium text-gray-900">Diversified Social Work Solutions</p>
                <p>Connecting licensed social workers with meaningful career opportunities.</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-gray-100">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-6">
                Send us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Your Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="mt-1"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="mt-1"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    className="mt-1"
                    required
                    data-testid="contact-subject-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Please describe your question or issue in detail..."
                    rows={6}
                    className="mt-1 resize-none"
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto bg-primary hover:bg-primary-hover text-white h-12 px-8"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
